import { parisNow } from "./tirageArticleQuery";

/**
 * Calculs du tableau de bord (espace connecté, lot 2, maquette du 16/10/2026), sans appel
 * réseau. Entrée : l'historique de my-draw-tonight, du plus récent au plus ancien
 * (30 tirages au plus), chaque entrée portant les numéros de la grille trouvés.
 * Une seule fenêtre pour toute la page : barres, moyenne, meilleur et forme des numéros
 * portent sur les mêmes FENETRE derniers tirages.
 */
export const FENETRE = 14;

/** Prochain tirage (20 h, heure de Paris) : demain si le tirage du jour est déjà en base. */
export function prochainTirage(dernier: string, now: Date = new Date()): "ce soir" | "demain" {
  return dernier !== "" && dernier >= parisNow(now).date ? "demain" : "ce soir";
}

/**
 * Nom affiché dans la salutation : rien quand il retombe sur l'adresse (partie locale,
 * « @ » ou « + »), pour ne jamais afficher une adresse tronquée en titre.
 */
export function nomSalutation(displayName: string | null | undefined, email?: string | null): string | null {
  const nom = (displayName ?? "").trim();
  if (nom === "" || /[@+]/.test(nom)) return null;
  const local = (email ?? "").split("@")[0]?.toLowerCase() ?? "";
  return local !== "" && nom.toLowerCase() === local ? null : nom;
}

export type TirageMonKeno = { date: string; matchCount: number; matched: number[] };

export type ResumeMonKeno = {
  /** Scores de la fenêtre, du plus ancien au plus récent (barres). */
  barres: { date: string; score: number }[];
  /** Moyenne sur la fenêtre, au dixième. */
  moyenne: number | null;
  nombreTirages: number;
  /** Meilleur score et ses dates, du plus récent au plus ancien. */
  meilleur: { score: number; dates: string[] } | null;
};

export function resumeMonKeno(historique: TirageMonKeno[]): ResumeMonKeno {
  const recents = historique.slice(0, FENETRE);
  const barres = [...recents].reverse().map((t) => ({ date: t.date, score: t.matchCount }));
  if (recents.length === 0) return { barres, moyenne: null, nombreTirages: 0, meilleur: null };
  const total = recents.reduce((s, t) => s + t.matchCount, 0);
  const max = Math.max(...recents.map((t) => t.matchCount));

  return {
    barres,
    moyenne: Math.round((total / recents.length) * 10) / 10,
    nombreTirages: recents.length,
    meilleur: { score: max, dates: recents.filter((t) => t.matchCount === max).map((t) => t.date) },
  };
}

export type NumerosEnCeMoment = {
  plusPresent: { numero: number; sorties: number } | null;
  /** absence = tirages sans le numéro depuis sa dernière sortie, tirage le plus récent compris. */
  plusAbsent: { numero: number; absence: number; auMoins: boolean } | null;
  /** Numéros de la grille sortis au moins une fois sur la fenêtre. */
  sortis: number;
  fenetre: number;
};

/** Le plus présent et le plus absent des numéros de la grille, sur l'historique fourni. */
export function numerosEnCeMoment(grille: number[], historique: TirageMonKeno[]): NumerosEnCeMoment {
  const recents = historique.slice(0, FENETRE);
  if (grille.length === 0 || recents.length === 0) return { plusPresent: null, plusAbsent: null, sortis: 0, fenetre: recents.length };

  const lignes = grille.map((numero) => {
    const sorties = recents.filter((t) => t.matched.includes(numero)).length;
    const index = recents.findIndex((t) => t.matched.includes(numero));
    return { numero, sorties, absence: index === -1 ? recents.length : index, auMoins: index === -1 };
  });
  // Égalités : le plus petit numéro, pour un affichage stable.
  const parPresence = [...lignes].sort((a, b) => b.sorties - a.sorties || a.numero - b.numero)[0];
  const parAbsence = [...lignes].sort((a, b) => b.absence - a.absence || a.numero - b.numero)[0];

  return {
    plusPresent: parPresence.sorties > 0 ? { numero: parPresence.numero, sorties: parPresence.sorties } : null,
    plusAbsent: parAbsence.absence > 0 ? { numero: parAbsence.numero, absence: parAbsence.absence, auMoins: parAbsence.auMoins } : null,
    sortis: lignes.filter((l) => l.sorties > 0).length,
    fenetre: recents.length,
  };
}

/** « 20 et 23 août », « 14 oct. », « 3, 9 et 12 sept. » : dates courtes, du plus ancien au plus récent. */
export function datesCourtes(dates: string[]): string {
  const mois = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
  const tri = [...dates].sort();
  const jours = tri.map((d) => ({ j: Number(d.slice(8, 10)), m: Number(d.slice(5, 7)) }));
  const memeMois = jours.every((x) => x.m === jours[0].m);
  const libelles = jours.map((x, i) => (memeMois && i < jours.length - 1 ? `${x.j}` : `${x.j} ${mois[x.m - 1]}`));
  if (libelles.length <= 1) return libelles[0] ?? "";

  return `${libelles.slice(0, -1).join(", ")} et ${libelles[libelles.length - 1]}`;
}

export type Fait = { type: string; text: string; priority: number };
/**
 * Trois faits de l'accueil : types différents d'abord (même règle que les faits publics), sans
 * le numéro déjà en tête de La Synthèse ou de Proches de leur record, puis complétés dans l'ordre.
 */
export function faitsAccueil(faits: Fait[], dejaVus: (number | undefined)[], n = 3): Fait[] {
  const numeros = new Set(dejaVus.filter((x): x is number => x !== undefined));
  const cite = (f: Fait) => (f.text.match(/\d+/g) ?? []).some((m) => numeros.has(Number(m)) && new RegExp(`\\b(le|du|de|les|des) ${m}\\b`).test(f.text));
  // Familles des étiquettes de la page d'un tirage (HighlightTone.php) : même étiquette, même famille.
  const alias: Record<string, string> = { streak_follower: "serie", streakFollower: "serie", twin_draw: "twin", twinDraw: "twin" };
  const famille = (t: string) => alias[t] ?? alias[t.replace(/s$/, "")] ?? t.replace(/s$/, "");
  const choisis: Fait[] = [];
  for (const f of faits) if (choisis.length < n && !cite(f) && !choisis.some((c) => famille(c.type) === famille(f.type))) choisis.push(f);
  for (const f of faits) if (choisis.length < n && !cite(f) && !choisis.includes(f)) choisis.push(f);
  for (const f of faits) if (choisis.length < n && !choisis.includes(f)) choisis.push(f);
  return choisis;
}
